import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { useSite, bookingUrl } from "@/context/SiteContext";

const DISMISS_KEY = "madn-popup-dismissed";

export const Popup = () => {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const popup = settings?.popup;

  useEffect(() => {
    if (!popup?.enabled) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    const t = setTimeout(() => setOpen(true), 1400);
    return () => clearTimeout(t);
  }, [popup?.enabled]);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-ink/70 backdrop-blur-sm grid place-items-center p-5"
          onClick={close}
          data-lenis-prevent
          data-testid="promo-popup-overlay"
        >
          <motion.div
            initial={{ y: 46, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-sand rounded-3xl max-w-md w-full overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.45)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="promo-popup"
          >
            <button
              onClick={close}
              data-testid="promo-popup-close"
              aria-label="Close"
              className="absolute top-4 right-4 z-10 h-9 w-9 grid place-items-center rounded-full bg-ink/60 text-sand backdrop-blur hover:bg-ink transition-colors"
            >
              <X size={16} />
            </button>
            {popup.image && (
              <img src={popup.image} alt={popup.title || "Mahadinusa offer"} className="w-full h-52 object-cover" data-testid="promo-popup-image" />
            )}
            <div className="p-7 sm:p-8 text-center">
              {popup.title && (
                <h3 className="font-serif text-2xl sm:text-3xl tracking-tight" data-testid="promo-popup-title">{popup.title}</h3>
              )}
              {popup.text && (
                <p className="mt-3 text-sm sm:text-base text-seastone leading-relaxed" data-testid="promo-popup-text">{popup.text}</p>
              )}
              <a
                href={popup.button_url || bookingUrl(settings)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="promo-popup-book-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-azure text-sand text-sm font-medium px-8 py-3.5 transition-all hover:bg-azure-dark hover:shadow-[0_0_0_6px_rgba(26,98,137,0.16)]"
              >
                {popup.button_label || "Book Now"} <ArrowUpRight size={15} />
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
